/** The Volunteers API from the app (1.1.0): the list, a card, the Companion tab, and the presses. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CompanionTabResponse, HoursResponse, PickerView, SendResult, TenantGateView, VolunteerCardView, VolunteersResponse } from '@shared/volunteers-api';
import type { CategoryKey } from '@shared/volunteers';
import { apiFetch } from '@/lib/api';

const vid = (id: string) => encodeURIComponent(id);

export function useVolunteers() {
  return useQuery({ queryKey: ['volunteers'], queryFn: () => apiFetch<VolunteersResponse>('/api/volunteers'), staleTime: 30_000, refetchOnWindowFocus: true });
}

export function useVolunteer(id: string | null) {
  return useQuery({ queryKey: ['volunteer', id], queryFn: () => apiFetch<VolunteerCardView>(`/api/volunteers/v/${vid(id!)}`), enabled: !!id, staleTime: 15_000, refetchOnWindowFocus: true });
}

export function useCompanionTab(enabled = true) {
  return useQuery({ queryKey: ['companion-tab'], queryFn: () => apiFetch<CompanionTabResponse>('/api/volunteers/companion'), enabled, staleTime: 30_000, refetchOnWindowFocus: true });
}

export function useHours(month: string) {
  return useQuery({ queryKey: ['volunteer-hours', month], queryFn: () => apiFetch<HoursResponse>(`/api/volunteers/hours?month=${month}`), staleTime: 60_000 });
}

export function useTenantGate(tenantId: string | null) {
  return useQuery({ queryKey: ['tenant-gate', tenantId], queryFn: () => apiFetch<TenantGateView>(`/api/volunteers/tenant-gate?tenantId=${encodeURIComponent(tenantId!)}`), enabled: !!tenantId, staleTime: 10_000 });
}

export function usePicker(q: { category: CategoryKey; dayKey: string; minutes: number; duration?: number } | null) {
  return useQuery({
    queryKey: ['volunteer-picker', q],
    queryFn: () => apiFetch<PickerView>(`/api/volunteers/picker?category=${q!.category}&dayKey=${q!.dayKey}&minutes=${q!.minutes}${q!.duration ? `&duration=${q!.duration}` : ''}`),
    enabled: !!q,
    staleTime: 10_000,
  });
}

/** After any press: the card (when the answer is one), the list and the Companion tab refresh. */
export function useVolunteerRefresh() {
  const qc = useQueryClient();
  return (card?: VolunteerCardView | null) => {
    if (card?.volunteerId) qc.setQueryData(['volunteer', card.volunteerId], card);
    void qc.invalidateQueries({ queryKey: ['volunteers'] });
    void qc.invalidateQueries({ queryKey: ['companion-tab'] });
    void qc.invalidateQueries({ queryKey: ['companion-attention'] });
  };
}

export const postJson = <T,>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
export const cardPost = (id: string, body: Record<string, unknown>) => postJson<VolunteerCardView>(`/api/volunteers/v/${vid(id)}`, body);
export const sendPress = (id: string, what: 'send-availability' | 'send-quickstart' | 'send-welcome' | 'send-review') => postJson<SendResult>(`/api/volunteers/v/${vid(id)}`, { what });

export { vid };
